/* Mock CodeBaby adapter. Deterministic. Same shape as the live adapter.
 * Drives the front-end UI and the grading lib without hitting the
 * CodeBaby CDN. Used in dev by default (CODEBABY_GUIDE.md §7) and in
 * tests.
 *
 * The mock is NOT trying to be LLM-realistic. It returns scripted
 * persona replies via simple pattern matching against the producer's
 * text. Variety is good enough to drive the role-play UI; the actual
 * "did the producer earn this?" evaluation lives in the grading lib,
 * which pattern-matches the producer's turns, not the persona's. */

import type {
  AvatarSession,
  MountOptions,
  MountResult,
  PersonaId,
} from './types';

interface ScriptedReply {
  /** Regex tested case-insensitively against the producer's last ask. */
  match: RegExp;
  /** One of these is picked round-robin per match. */
  replies: string[];
}

interface PersonaScript {
  /** Persona's opening line. Pushed via playResponse on first ask if
   *  startData.mode === 'roleplay' AND the host code calls
   *  `session.playResponse(<opener>)` itself. The mock does not auto-fire
   *  this — that respects the single-source rule (§5). */
  opener: string;
  rules: ScriptedReply[];
  /** Default reply if no rule matches. */
  fallback: string;
}

const DAN_SCRIPT: PersonaScript = {
  opener: 'Hi — I\'m Dan. Every agent I\'ve talked to says they \'go above and beyond\' — that\'s not an answer, that\'s a slogan. What specifically do you do that the two-percent guy on Zillow doesn\'t?',
  rules: [],
  fallback: 'Every agent I\'ve talked to says they \'go above and beyond\' — that\'s not an answer, that\'s a slogan. What specifically do you do that the two-percent guy on Zillow doesn\'t?',
};

const RITA_SCRIPT: PersonaScript = {
  opener: 'Hi — I\'m Rita. Marcus told me the exact same thing about being different. What I need is not a promise — I need you to show me the math on paper, line by line, before I sign anything.',
  rules: [],
  fallback: 'Marcus told me the exact same thing about being different. What I need is not a promise — I need you to show me the math on paper, line by line, before I sign anything.',
};

const MAYA_SCRIPT: PersonaScript = {
  opener: 'Hi — I\'m Maya. I know it\'s technically over my approval amount, but the seller might come down a little, right? And I could ask them to cover closing costs — people do that all the time.',
  rules: [],
  fallback: 'I know it\'s technically over my approval amount, but the seller might come down a little, right? And I could ask them to cover closing costs — people do that all the time.',
};

const TYLER_SCRIPT: PersonaScript = {
  opener: 'Hi — I\'m Tyler. I already ran the numbers like five times — $304,900 fits inside my $310K approval, so I\'m good, right? Can we just start the paperwork?',
  rules: [],
  fallback: 'I already ran the numbers like five times — $304,900 fits inside my $310K approval, so I\'m good, right? Can we just start the paperwork?',
};

const COACH_SCRIPT: PersonaScript = {
  opener: 'Welcome back. Let\'s look at how that conversation went.',
  rules: [],
  fallback: 'Slow down, ask one specific question, and let the answer guide you.',
};

const SCRIPTS: Record<PersonaId, PersonaScript> = {
  'dan-kowalski': DAN_SCRIPT,
  'rita-solano': RITA_SCRIPT,
  'maya-osei': MAYA_SCRIPT,
  'tyler-brandt': TYLER_SCRIPT,
  coach: COACH_SCRIPT,
};

interface MockHandlers {
  ruleHits: Map<RegExp, number>;
  scheduled: Set<ReturnType<typeof setTimeout>>;
}

function pickReply(
  rule: ScriptedReply,
  handlers: MockHandlers,
): string {
  const seen = handlers.ruleHits.get(rule.match) ?? 0;
  const reply = rule.replies[seen % rule.replies.length] ?? rule.replies[0] ?? '';
  handlers.ruleHits.set(rule.match, seen + 1);
  return reply;
}

function matchReply(
  script: PersonaScript,
  producerText: string,
  handlers: MockHandlers,
): string {
  for (const rule of script.rules) {
    if (rule.match.test(producerText)) {
      return pickReply(rule, handlers);
    }
  }
  return script.fallback;
}

/** Async tick used by the mock. Non-zero in dev (mimics LLM latency
 *  loosely so the UI doesn't feel instant). Tests can pass an override
 *  via the optional `mockDelayMs` field on MountOptions if added later. */
const MOCK_TICK_MS = import.meta.env.MODE === 'test' ? 0 : 60;

export function mountAvatarMock(opts: MountOptions): MountResult {
  const personaId = (opts.startData.personaId ?? 'priya-anand') as PersonaId;
  const script = SCRIPTS[personaId] ?? DAN_SCRIPT;

  const handlers: MockHandlers = {
    ruleHits: new Map(),
    scheduled: new Set(),
  };

  let unmounted = false;
  let micState: 'idle' | 'listening' = 'idle';

  function schedule(fn: () => void, ms: number): void {
    const handle = setTimeout(() => {
      handlers.scheduled.delete(handle);
      if (!unmounted) fn();
    }, ms);
    handlers.scheduled.add(handle);
  }

  // Fire playerBuilt asynchronously, mimicking the real loader's mount cycle.
  schedule(() => {
    opts.onPlayerBuilt?.();
  }, MOCK_TICK_MS);

  const session: AvatarSession = {
    ask(text: string) {
      if (unmounted) return;
      const reply = matchReply(script, text, handlers);
      schedule(() => {
        opts.onResponse?.({ text: reply });
      }, MOCK_TICK_MS);
    },

    playResponse(text: string) {
      if (unmounted) return;
      // System-owned line — bypasses the rule matcher. Single-source rule.
      schedule(() => {
        opts.onResponse?.({ text });
        opts.onSegmentEnded?.();
      }, MOCK_TICK_MS);
    },

    toggleMicrophone() {
      if (unmounted) return;
      if (micState === 'idle') {
        micState = 'listening';
        schedule(() => opts.onMicrophoneStarted?.(), 0);
        schedule(() => opts.onMicrophoneReady?.(), 30);
      } else {
        micState = 'idle';
        schedule(() => opts.onMicrophoneStopped?.(), 0);
      }
    },

    mute() {
      // No-op in mock.
    },

    unmute() {
      // No-op in mock.
    },

    resetConversation() {
      handlers.ruleHits.clear();
    },

    pushContext(_context: Record<string, unknown>) {
      // No-op in mock. Live adapter fires na-conversationalContextData.vidbaby.
    },

    unmount() {
      unmounted = true;
      handlers.scheduled.forEach((t) => clearTimeout(t));
      handlers.scheduled.clear();
      // Do NOT touch window state — see §3 (don't tear down vidbaby/jQuery).
    },
  };

  return { session };
}

/** Test helper — exposed for unit tests to inspect canned openers. */
export function getMockOpener(personaId: PersonaId): string {
  return SCRIPTS[personaId]?.opener ?? '';
}
